// ==================== CLIENT SUPABASE AUTH ====================
// Client Supabase pour l'authentification
// Remplace l'authentification custom par Supabase Auth

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://srvscuamckwkblbjqbjs.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!SUPABASE_ANON_KEY) {
  console.warn('⚠️ [SUPABASE AUTH] SUPABASE_ANON_KEY non défini dans les variables d\'environnement');
}

// Créer le client Supabase
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

/**
 * Connexion avec email et mot de passe
 */
export const loginWithSupabase = async (email, password) => {
  try {
    console.log('🔄 [SUPABASE AUTH] Tentative de connexion...');
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password: password
    });

    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur connexion:', error.message);
      throw new Error(error.message);
    }

    if (!data.session || !data.user) {
      throw new Error('Aucune session retournée');
    }

    console.log('✅ [SUPABASE AUTH] Connexion réussie');

    // Stocker le token dans localStorage (compatible avec l'architecture existante)
    localStorage.setItem('token', data.session.access_token);
    
    // Stocker les infos utilisateur
    const userData = {
      id: data.user.id,
      email: data.user.email,
      // Les custom claims (company_id, role) sont dans le JWT
      // On les récupère depuis le token décodé ou depuis l'API
    };

    return {
      success: true,
      user: userData,
      token: data.session.access_token,
      session: data.session
    };
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    throw error;
  }
};

/**
 * Inscription d'un nouvel utilisateur
 */
export const registerWithSupabase = async (email, password, userData = {}) => {
  try {
    console.log('🔄 [SUPABASE AUTH] Tentative d\'inscription...');
    
    const { data, error } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password: password,
      options: {
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          company_id: userData.companyId,
          role: userData.role || 'employee'
        }
      }
    });

    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur inscription:', error.message);
      throw new Error(error.message);
    }

    console.log('✅ [SUPABASE AUTH] Inscription réussie');

    // Si l'email nécessite confirmation, data.session peut être null
    if (data.session) {
      localStorage.setItem('token', data.session.access_token);
    }

    return {
      success: true,
      user: data.user,
      session: data.session,
      needsEmailConfirmation: !data.session
    };
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    throw error;
  }
};

/**
 * Déconnexion
 */
export const logoutWithSupabase = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur déconnexion:', error.message);
      throw error;
    }
    
    // Nettoyer localStorage
    localStorage.removeItem('token');
    
    return { success: true };
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    throw error;
  }
};

/**
 * Récupérer la session actuelle
 */
export const getCurrentSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur récupération session:', error.message);
      return null;
    }

    if (session) {
      // Synchroniser avec localStorage
      localStorage.setItem('token', session.access_token);
      return session;
    }

    return null;
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    return null;
  }
};

/**
 * Récupérer l'utilisateur actuel
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur récupération utilisateur:', error.message);
      return null;
    }

    return user;
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    return null;
  }
};

/**
 * Rafraîchir le token
 */
export const refreshSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur rafraîchissement:', error.message);
      throw error;
    }

    if (session) {
      localStorage.setItem('token', session.access_token);
    }

    return session;
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    throw error;
  }
};

/**
 * Réinitialiser le mot de passe
 */
export const resetPassword = async (email) => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
      redirectTo: `${window.location.origin}/reset-password`
    });

    if (error) {
      console.error('❌ [SUPABASE AUTH] Erreur réinitialisation:', error.message);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('❌ [SUPABASE AUTH] Erreur:', error);
    throw error;
  }
};

export default supabase;


